import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand, useBrandColors } from "../brand/BrandContext";
import { resolveBrandContacts } from "../brand/contacts";
import { socialLinks } from "../brand/socialLinks";
import { scrollLandingToTop } from "../utils/ScrollToTop";

const linkColor = "rgba(255, 255, 255, 0.8)";

const Footer = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const brand = useBrand();
  const BRAND = useBrandColors();
  const contacts = resolveBrandContacts(brand);
  const SOCIAL_LINKS = socialLinks(contacts);
  const FOOTER_LINKS = {
    documents: [
      { to: "/terms-and-conditions", labelKey: "footer.termsAndConditions" },
      { to: "/privacy-policy", labelKey: "footer.privacyPolicy" },
      { to: "/offer", labelKey: "footer.publicOffer" },
    ],
    info: [
      { to: "/faq", labelKey: "footer.faq" },
      { to: "/contact", labelKey: "footer.contacts" },
    ],
  };
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: BRAND.primary,
        color: "white",
        py: 6,
        px: 2,
        mt: "auto",
      }}
      aria-label={t("footer.footerLabel")}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              color="inherit"
              component={RouterLink}
              to="/"
              onClick={(event) => scrollLandingToTop(event, location.pathname)}
              sx={{ p: 0, minWidth: 0 }}
              aria-label={t("footer.toHome")}
            >
              {brand.logoSrc ? (
                <Box
                  component="img"
                  src={brand.logoSrc}
                  alt={brand.name}
                  sx={{ height: 56 }}
                />
              ) : (
                <Typography variant="h6" fontWeight={700} color="inherit">
                  {brand.name}
                </Typography>
              )}
            </Button>
            <Typography variant="body2" sx={{ mt: 1, color: linkColor }}>
              {t("footer.tagline")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight="bold" color="inherit">
              {t("footer.documents")}
            </Typography>
            {FOOTER_LINKS.documents.map(({ to, labelKey }) => (
              <Typography
                key={to}
                component={RouterLink}
                to={to}
                variant="body2"
                sx={{
                  display: "block",
                  mt: 1,
                  color: linkColor,
                  textDecoration: "none",
                  "&:hover": { color: "white", textDecoration: "underline" },
                }}
              >
                {t(labelKey)}
              </Typography>
            ))}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight="bold" color="inherit">
              {t("footer.info")}
            </Typography>
            {FOOTER_LINKS.info.map(({ to, labelKey }) => (
              <Typography
                key={to}
                component={RouterLink}
                to={to}
                variant="body2"
                sx={{
                  display: "block",
                  mt: 1,
                  color: linkColor,
                  textDecoration: "none",
                  "&:hover": { color: "white", textDecoration: "underline" },
                }}
              >
                {t(labelKey)}
              </Typography>
            ))}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight="bold" color="inherit">
              {t("footer.contacts")}
            </Typography>
            {contacts.phones.map((phone) => (
              <Typography
                key={phone.tel}
                variant="body2"
                component="a"
                href={`tel:${phone.tel}`}
                sx={{
                  mt: 1,
                  color: linkColor,
                  display: "block",
                  textDecoration: "none",
                  "&:hover": { color: "white", textDecoration: "underline" },
                }}
              >
                {phone.display}
              </Typography>
            ))}
            {contacts.email ? (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: linkColor,
                display: "block",
                textDecoration: "none",
                "&:hover": { color: "white", textDecoration: "underline" },
              }}
              component="a"
              href={`mailto:${contacts.email}`}
            >
              E-mail: {contacts.email}
            </Typography>
            ) : null}
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {SOCIAL_LINKS.map(({ href, label, Icon }) => (
            <IconButton
              key={label}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              sx={{
                color: linkColor,
                "&:hover": { color: "white", backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Icon />
            </IconButton>
          ))}
        </Box>

        <Box
          sx={{
            mt: 4,
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.2)",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: linkColor }}>
            © {new Date().getFullYear()} {brand.name}. {t("footer.copyright")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
